/**
 * IndexedDB wrapper for offline data caching
 * Stores customers, products, inventory, and pending transactions
 */

const DB_NAME = 'van-sales-offline'
// v2 added PRODUCTS_WITH_UOMS. The upgrade handler only ever CREATES
// missing stores, so existing data — including the unsynced transaction
// queue — survives the bump untouched.
const DB_VERSION = 2

// Store names
export const STORES = {
  CUSTOMERS: 'customers',
  PRODUCTS: 'products',
  // Kept separate from PRODUCTS on purpose: /products and
  // /products/with-uoms return DIFFERENT shapes, and sharing one store
  // would let a plain-product write strip the uoms[] that New Sale needs
  // to price a box.
  PRODUCTS_WITH_UOMS: 'products_with_uoms',
  SUPPLIERS: 'suppliers',
  INVENTORY: 'inventory',
  PENDING_TRANSACTIONS: 'pending_transactions',
  CACHE_META: 'cache_meta'
} as const

type StoreName = typeof STORES[keyof typeof STORES]

interface CacheMeta {
  store: string
  lastUpdated: string
  count: number
}

let dbInstance: IDBDatabase | null = null

/**
 * Open or create the IndexedDB database
 */
function openDB(): Promise<IDBDatabase> {
  if (dbInstance) {
    return Promise.resolve(dbInstance)
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => {
      console.error('Failed to open IndexedDB:', request.error)
      reject(request.error)
    }

    // Another tab is asking to upgrade (e.g. a release that adds a store):
    // let go of this connection so it can, instead of holding it hostage.
    request.onblocked = () => {
      reject(new Error('IndexedDB upgrade blocked by another open tab'))
    }

    request.onsuccess = () => {
      dbInstance = request.result
      // Without this, an older tab's open connection blocks a newer tab's
      // version upgrade forever: onblocked never resolves, so every cache
      // read AND every queued sale hangs on a promise that never settles.
      dbInstance.onversionchange = () => {
        dbInstance?.close()
        dbInstance = null
      }
      resolve(dbInstance)
    }

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result

      // Customers store
      if (!db.objectStoreNames.contains(STORES.CUSTOMERS)) {
        const customerStore = db.createObjectStore(STORES.CUSTOMERS, { keyPath: 'customer_id' })
        customerStore.createIndex('name', 'name', { unique: false })
      }

      // Products store
      if (!db.objectStoreNames.contains(STORES.PRODUCTS)) {
        const productStore = db.createObjectStore(STORES.PRODUCTS, { keyPath: 'product_id' })
        productStore.createIndex('name', 'name', { unique: false })
      }

      // Products-with-UOMs store (what the sale and purchase screens read)
      if (!db.objectStoreNames.contains(STORES.PRODUCTS_WITH_UOMS)) {
        const pu = db.createObjectStore(STORES.PRODUCTS_WITH_UOMS, { keyPath: 'product_id' })
        pu.createIndex('name', 'name', { unique: false })
      }

      // Suppliers store
      if (!db.objectStoreNames.contains(STORES.SUPPLIERS)) {
        const supplierStore = db.createObjectStore(STORES.SUPPLIERS, { keyPath: 'supplier_id' })
        supplierStore.createIndex('name', 'name', { unique: false })
      }

      // Inventory store (van inventory)
      if (!db.objectStoreNames.contains(STORES.INVENTORY)) {
        const inventoryStore = db.createObjectStore(STORES.INVENTORY, { keyPath: 'product_id' })
        inventoryStore.createIndex('quantity', 'quantity', { unique: false })
      }

      // Pending transactions store (for offline queue)
      if (!db.objectStoreNames.contains(STORES.PENDING_TRANSACTIONS)) {
        const txStore = db.createObjectStore(STORES.PENDING_TRANSACTIONS, {
          keyPath: 'id',
          autoIncrement: true
        })
        txStore.createIndex('created_at', 'created_at', { unique: false })
        txStore.createIndex('type', 'type', { unique: false })
        txStore.createIndex('status', 'status', { unique: false })
      }

      // Cache metadata store
      if (!db.objectStoreNames.contains(STORES.CACHE_META)) {
        db.createObjectStore(STORES.CACHE_META, { keyPath: 'store' })
      }
    }
  })
}

/**
 * Get all items from a store
 */
export async function getAll<T>(storeName: StoreName): Promise<T[]> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly')
    const store = transaction.objectStore(storeName)
    const request = store.getAll()

    request.onsuccess = () => resolve(request.result as T[])
    request.onerror = () => reject(request.error)
  })
}

/**
 * Get a single item by key
 */
export async function get<T>(storeName: StoreName, key: IDBValidKey): Promise<T | undefined> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly')
    const store = transaction.objectStore(storeName)
    const request = store.get(key)

    request.onsuccess = () => resolve(request.result as T | undefined)
    request.onerror = () => reject(request.error)
  })
}

/**
 * Put a single item (insert or update)
 */
export async function put<T>(storeName: StoreName, item: T): Promise<void> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite')
    const store = transaction.objectStore(storeName)
    const request = store.put(item)

    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}

/**
 * Put multiple items (bulk insert/update)
 */
export async function putAll<T>(storeName: StoreName, items: T[]): Promise<void> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite')
    const store = transaction.objectStore(storeName)

    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)

    for (const item of items) {
      store.put(item)
    }
  })
}

/**
 * Delete an item by key
 */
export async function remove(storeName: StoreName, key: string | number): Promise<void> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite')
    const store = transaction.objectStore(storeName)
    const request = store.delete(key)

    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}

/**
 * Clear all items in a store
 */
export async function clear(storeName: StoreName): Promise<void> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite')
    const store = transaction.objectStore(storeName)
    const request = store.clear()

    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}

/**
 * Count items in a store
 */
export async function count(storeName: StoreName): Promise<number> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly')
    const store = transaction.objectStore(storeName)
    const request = store.count()

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

/**
 * Update cache metadata
 */
export async function updateCacheMeta(storeName: StoreName, itemCount: number): Promise<void> {
  const meta: CacheMeta = {
    store: storeName,
    lastUpdated: new Date().toISOString(),
    count: itemCount
  }
  await put(STORES.CACHE_META, meta)
}

/**
 * Get cache metadata for a store
 */
export async function getCacheMeta(storeName: StoreName): Promise<CacheMeta | undefined> {
  return get<CacheMeta>(STORES.CACHE_META, storeName)
}

/**
 * Check if cache is stale (older than maxAge in minutes)
 */
export async function isCacheStale(storeName: StoreName, maxAgeMinutes: number = 30): Promise<boolean> {
  const meta = await getCacheMeta(storeName)
  if (!meta) return true

  const lastUpdated = new Date(meta.lastUpdated)
  const now = new Date()
  const diffMinutes = (now.getTime() - lastUpdated.getTime()) / (1000 * 60)

  return diffMinutes > maxAgeMinutes
}

/**
 * Cache data with metadata — clears and repopulates in a single transaction
 * to avoid a window where the store is empty.
 */
export async function cacheData<T>(storeName: StoreName, data: T[]): Promise<void> {
  const db = await openDB()
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite')
    const store = transaction.objectStore(storeName)

    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)

    store.clear()
    for (const item of data) {
      store.put(item)
    }
  })
  await updateCacheMeta(storeName, data.length)
}

/**
 * Get cached data if available and not stale
 */
export async function getCachedData<T>(
  storeName: StoreName,
  maxAgeMinutes: number = 30
): Promise<T[] | null> {
  const isStale = await isCacheStale(storeName, maxAgeMinutes)
  if (isStale) return null

  const data = await getAll<T>(storeName)
  return data.length > 0 ? data : null
}

// ============================================
// INVENTORY ADJUSTMENT FUNCTIONS
// For offline sales/purchases to update local inventory
// ============================================

interface InventoryItem {
  product_id: string
  product_name?: string
  quantity: number
  selling_price?: number
}

interface SaleItem {
  product_id: string
  quantity: number
  /** When the sale was made in a non-base UOM, this is the multiplier to
   *  reach base units. Defaults to 1. Critical for keeping IDB inventory
   *  in step with the server, which stores all stock in base units. */
  conversion_factor?: number
}

interface PurchaseItem {
  product_id: string
  quantity: number
  /** Base units per `quantity`. A purchase is entered in the buying UOM
   *  (2 boxes), while this cache stores BASE units — so it must be applied
   *  exactly as the sale side does. */
  conversion_factor?: number
}

/**
 * Deduct inventory for an offline sale — uses a single IDB transaction
 * to ensure atomicity (check + deduct cannot be interleaved).
 * Returns true if successful, false if insufficient inventory.
 */
export async function deductInventoryForSale(items: SaleItem[]): Promise<boolean> {
  try {
    const db = await openDB()

    return await new Promise<boolean>((resolve, reject) => {
      const transaction = db.transaction(STORES.INVENTORY, 'readwrite')
      const store = transaction.objectStore(STORES.INVENTORY)

      // Read all inventory within THIS transaction
      const getAllReq = store.getAll()

      getAllReq.onsuccess = () => {
        const inventory = getAllReq.result as InventoryItem[]
        const inventoryMap = new Map(inventory.map(i => [i.product_id, i]))

        // IDB inventory is stored in base units. Each item.quantity is in
        // the selected UOM, so multiply by conversion_factor (defaults to 1
        // for base-unit sales). Without this, multi-UOM sales drift IDB
        // from server on the next sync — IDB removes the UOM count,
        // server removes the base count.
        const stockQtyFor = (item: SaleItem) => item.quantity * (item.conversion_factor || 1)

        // Check if we have enough inventory for all items (in base units)
        for (const item of items) {
          const inv = inventoryMap.get(item.product_id)
          const needed = stockQtyFor(item)
          if (!inv || inv.quantity < needed) {
            console.warn(`[IDB] Insufficient inventory for product ${item.product_id}`)
            transaction.abort()
            resolve(false)
            return
          }
        }

        // Deduct all items within the same transaction (base units)
        for (const item of items) {
          const inv = inventoryMap.get(item.product_id)!
          inv.quantity -= stockQtyFor(item)
          store.put(inv)
        }
      }

      getAllReq.onerror = () => reject(getAllReq.error)

      transaction.oncomplete = () => {
        resolve(true)
      }
      transaction.onerror = () => {
        // If aborted due to insufficient inventory, we already resolved false
        if (!transaction.error) return
        reject(transaction.error)
      }
      transaction.onabort = () => {
        // Resolve false if not already resolved (handles the abort from insufficient check)
      }
    })
  } catch (error) {
    console.error('[IDB] Failed to deduct inventory:', error)
    return false
  }
}

/**
 * Run a callback inside a single readwrite IDB transaction across all rows
 * of STORES.INVENTORY. Either every change in the callback commits, or
 * none do — mirrors the deductInventoryForSale pattern so multi-item
 * mutations are atomic even if the tab is killed mid-loop.
 */
async function applyInventoryAtomic(mutate: (map: Map<string, InventoryItem>) => void): Promise<boolean> {
  try {
    const db = await openDB()
    return await new Promise<boolean>((resolve, reject) => {
      const transaction = db.transaction(STORES.INVENTORY, 'readwrite')
      const store = transaction.objectStore(STORES.INVENTORY)
      const getAllReq = store.getAll()
      getAllReq.onsuccess = () => {
        const items = getAllReq.result as InventoryItem[]
        const map = new Map(items.map(i => [i.product_id, i]))
        mutate(map)
        for (const inv of map.values()) {
          store.put(inv)
        }
      }
      getAllReq.onerror = () => reject(getAllReq.error)
      transaction.oncomplete = () => resolve(true)
      transaction.onerror = () => reject(transaction.error)
      transaction.onabort = () => resolve(false)
    })
  } catch (error) {
    console.error('[IDB] Atomic inventory mutation failed:', error)
    return false
  }
}

/**
 * Add inventory for an offline purchase (atomic).
 */
export async function addInventoryForPurchase(items: PurchaseItem[]): Promise<boolean> {
  return applyInventoryAtomic((map) => {
    for (const item of items) {
      // Base units, mirroring deductInventoryForSale. This used to add the
      // raw quantity, so buying 2 boxes of 24 offline credited the van with
      // 2 units instead of 48 — and the oversell guard then refused to sell
      // stock the driver had just loaded.
      const baseQty = item.quantity * (item.conversion_factor || 1)
      const existing = map.get(item.product_id)
      if (existing) {
        existing.quantity += baseQty
      } else {
        // Minimal new row; serverside will reconcile on sync.
        map.set(item.product_id, { product_id: item.product_id, quantity: baseQty } as InventoryItem)
      }
    }
  })
}

/**
 * Restore inventory if an offline sale fails to sync (atomic).
 */
export async function restoreInventoryForFailedSale(items: SaleItem[]): Promise<void> {
  await applyInventoryAtomic((map) => {
    for (const item of items) {
      const inv = map.get(item.product_id)
      if (inv) {
        // Restore in base units to mirror deductInventoryForSale.
        inv.quantity += item.quantity * ((item as any).conversion_factor || 1)
      }
    }
  })
}

/**
 * Restore inventory if an offline purchase fails to sync (atomic).
 */
export async function restoreInventoryForFailedPurchase(items: PurchaseItem[]): Promise<void> {
  await applyInventoryAtomic((map) => {
    for (const item of items) {
      const inv = map.get(item.product_id)
      if (inv) {
        // Same base-unit conversion as the add it reverses.
        const newQty = inv.quantity - item.quantity * (item.conversion_factor || 1)
        if (newQty < 0) {
          console.warn(`[IDB] Inventory inconsistency for ${item.product_id}: ${newQty}, clamping to 0`)
        }
        inv.quantity = Math.max(0, newQty)
      }
    }
  })
}

// Export db operations for direct use
export const idb = {
  getAll,
  get,
  put,
  putAll,
  remove,
  clear,
  count,
  cacheData,
  getCachedData,
  getCacheMeta,
  isCacheStale,
  deductInventoryForSale,
  addInventoryForPurchase,
  restoreInventoryForFailedSale,
  restoreInventoryForFailedPurchase,
  STORES
}

export default idb
